import React, { useCallback, useEffect, useMemo, useState } from "react";
import TraceNode, { traceNodeKey } from "./TraceNode";
import ComposerReviewPanel from "./ComposerReviewPanel";
import { buildJobTrace, buildLineTrace } from "./traceModel";
import { getRateCards } from "../../../services/apiService";

/**
 * The right pane: where the number came from.
 *
 * Every dollar expands to the formula that produced it and to the record that
 * holds the constant — the material sheet, the rate card, the finish rate, the
 * pricing tier. Nothing here recomputes a price; it renders the descriptors
 * traceModel.js derived from the backend's own provenance fields.
 *
 * The finish catalog is fetched once per mount so Andy's gallon math
 * (coats x $/gal / coverage) can be shown. If that request fails the finish
 * node degrades to the $/sq ft the backend already returned — it never
 * invents coats or coverage.
 */

function EmptyTrace({ message }) {
  return <p className="wb-empty">{message}</p>;
}

function CostTraceInspector({
  scope = "line",
  item = null,
  lineIndex = 0,
  results = null,
  formData = {},
  onClearLaborOverride,
  onFocusLaborOverride,
  onOpenTier,
  loadFinishRates = getRateCards,
  draft = null,
  resultsStale = false,
}) {
  const [finishRatesById, setFinishRatesById] = useState({});

  // A node the estimator closed stays closed on the next line too — and one
  // they opened stays open. Session-scoped on purpose: nothing persists.
  const [openState, setOpenState] = useState({});
  const handleOpenChange = useCallback((nodeId, isOpen) => {
    setOpenState((prev) =>
      prev[nodeId] === isOpen ? prev : { ...prev, [nodeId]: isOpen },
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (typeof loadFinishRates !== "function") return undefined;

    Promise.resolve()
      .then(() => loadFinishRates())
      .then((payload) => {
        if (cancelled) return;
        const byId = {};
        (payload?.finish_rates || []).forEach((rate) => {
          if (rate && rate.id !== undefined && rate.id !== null) {
            byId[rate.id] = rate;
          }
        });
        setFinishRatesById(byId);
      })
      .catch(() => {
        /* Degrade to the backend's $/sq ft — never fabricate the gallon math. */
      });

    return () => {
      cancelled = true;
    };
  }, [loadFinishRates]);

  const nodes = useMemo(() => {
    if (scope === "job") return buildJobTrace(results, formData);
    return buildLineTrace(
      item,
      results || {},
      formData,
      finishRatesById,
      lineIndex,
    );
  }, [scope, item, lineIndex, results, formData, finishRatesById]);

  const handleAction = (actionId) => {
    if (actionId === "clear-labor-override" && onClearLaborOverride) {
      onClearLaborOverride();
    } else if (actionId === "focus-labor-override" && onFocusLaborOverride) {
      onFocusLaborOverride();
    } else if (actionId === "open-tier" && onOpenTier) {
      onOpenTier();
    }
  };

  // The draft's reasoning renders whether or not the line has priced yet — a
  // freshly landed draft has no results at all, and its rationale is exactly
  // what the estimator needs before pressing Calculate.
  const reviewPanel =
    scope === "line" && draft ? <ComposerReviewPanel draft={draft} /> : null;

  const trace = !results ? (
    <EmptyTrace message="Run the pricing to see where each dollar comes from." />
  ) : nodes.length === 0 ? (
    <EmptyTrace
      message={
        scope === "job"
          ? "No job totals yet."
          : resultsStale
            ? "Pricing this line…"
            : "This line has not priced yet."
      }
    />
  ) : (
    nodes.map((node) => (
      <TraceNode
        key={traceNodeKey(node, `${scope}-${lineIndex}`)}
        node={node}
        onAction={handleAction}
        openState={openState}
        onOpenChange={handleOpenChange}
        lineKey={`${scope}-${lineIndex}`}
      />
    ))
  );

  return (
    <div className="wb-trace">
      {reviewPanel}
      {trace}
    </div>
  );
}

export default CostTraceInspector;
